import React, { useState, useRef } from 'react';
import { apiService } from '../services/api';
import { ReservationData } from '../App';
import { AvailableSlot } from './SlotSelection';
import './Results.css';

interface ResultsProps {
  reservationData: ReservationData | null;
  selectedSlot: AvailableSlot | null;
  onBackToScanner: () => void;
}

const Results: React.FC<ResultsProps> = ({ reservationData, selectedSlot, onBackToScanner }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  // Ref for immediate blocking (useState is async and can allow double-clicks)
  const isProcessingRef = useRef(false);

  if (!reservationData || !selectedSlot) {
    return (
      <div className="results-container">
        <div className="results-card">
          <div className="no-data">
            <p>Nessun dato disponibile.</p>
            <button className="back-button" onClick={onBackToScanner}>
              Torna allo scan
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cancelScan = async () => {
    // Immediate sync check to prevent double-clicks
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      // Log the cancellation (is_accepted = false)
      const payload = {
        guestData: {
          reservation: reservationData,
          selected_slot: selectedSlot
        },
        is_accepted: false
      };

      const response = await apiService.consumeMealWithSlot(payload);

      if (response.code === 200) {
        console.log('Cancellation logged successfully');
        onBackToScanner();
      } else {
        alert(`Errore durante l'annullamento: ${response.message}`);
        onBackToScanner(); // Navigate anyway to avoid being stuck
      }
    } catch (error) {
      console.error('Error logging cancellation:', error);
      // Still navigate back even if logging fails
      onBackToScanner();
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  };

  const consumeMeal = async () => {
    // Immediate sync check to prevent double-clicks
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      // Log the acceptance and consumption (is_accepted = true)
      const payload = {
        guestData: {
          reservation: reservationData,
          selected_slot: selectedSlot
        },
        is_accepted: true
      };

      const response = await apiService.consumeMealWithSlot(payload);

      if (response.code === 200) {
        alert(`Pasto "${selectedSlot.slot_name}" utilizzato con successo!`);
        onBackToScanner();
      } else {
        alert(`Errore: ${response.message}`);
      }
    } catch (error: any) {
      console.error('Error consuming meal:', error);
      const msg = error.message || 'Errore durante l\'utilizzo del pasto';
      alert(msg);
      if (msg.includes('Limite pasti')) {
        onBackToScanner();
      }
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    window.location.reload();
  };

  // Get customer type label based on discount type
  const getCustomerTypeLabel = () => {
    switch (selectedSlot.discount_type) {
      case 'inps':
        return 'INPS';
      case 'oneyear':
        return 'Annuale';
      case 'sixmonths':
        return 'Semestrale';
      default:
        return selectedSlot.discount_type;
    }
  };

  return (
    <div className="results-container">
      <div className="results-header">
        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      <div className="results-card">
        <div className="success-icon">✅</div>
        <h2>Riepilogo Pasto</h2>

        <div className="guest-info">
          {reservationData.customer_name && (
            <div className="info-item">
              <label>Cliente:</label>
              <span className="info-value">{reservationData.customer_name}</span>
            </div>
          )}

          <div className="info-item">
            <label>Pasto:</label>
            <span className="info-value highlight">{selectedSlot.slot_name}</span>
          </div>

          <div className="info-item">
            <label>Tipo Cliente:</label>
            <span className="info-value">
              {getCustomerTypeLabel()}
            </span>
          </div>

          <div className="pricing-section">
            {(selectedSlot.discount_type === 'oneyear' || selectedSlot.discount_type === 'sixmonths') ? (
              <div className="info-item final-price">
                <label>Sconto:</label>
                <span className="final-price-value">{selectedSlot.discount_percentage}%</span>
              </div>
            ) : (
              <div className="info-item final-price">
                <label>Prezzo Finale:</label>
                <span className="final-price-value">€{selectedSlot.final_price.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="secondary-button"
            onClick={cancelScan}
            disabled={isProcessing}
          >
            Annulla
          </button>
          <button
            className="primary-button"
            onClick={consumeMeal}
            disabled={isProcessing}
          >
            {isProcessing ? 'Elaborazione...' : 'Conferma Erogazione'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Results;
